import { useRouter } from 'next/router';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { createContext, useState, useRef, useEffect, useContext } from 'react';
import ReactGA from 'react-ga4';
import moment from 'moment';
import * as queryString from 'query-string';

import { PaymentError } from 'components/Checkout/Page/PaymentError';
import useIsMobile from 'hooks/useIsMobile';
import usePlan from 'hooks/usePlan';
import { useAuthUser } from 'features/auth/AuthUserContext';

import { PaymentMethodEnum, BillingIntervalEnum, PlanEnum } from 'enums';
import PaymentService from 'services/PaymentService';
import ProfileService from 'services/ProfileService';
import getCleverTap from 'utils/clevertap';
import {
  checkLocaleCouponForUserCountry,
  getLocaleData,
  getPlansPricingData,
  resolveBillingInterval,
  resolvePlan,
} from 'utils/plan';
import { DEFAULT_LOCALE_ID } from 'utils/locale';
import { StripeProvider } from './StripeContext';

const initialState = {
  data: {
    isBusy: false,
    errorMessage: '',
    user: {
      name: '',
      email: '',
      countryCode: '',
      ready: false,
    },
    coupon: {
      status: '',
      gateway: '',
      plan: '',
      billingInterval: '',
      percent: 0,
    },
    profile: {
      whatsappNumber: '',
    },
    planData: {},
    paymentService: null,
    profileService: null,
    paymentMethod: PaymentMethodEnum.CARD,
    billingInterval: BillingIntervalEnum.YEAR,
    teamSize: 1,
    stripe: null,
    stripeElements: null,
    isMobile: false,
    currentPlan: '',
    formRef: null,
    hasFormError: false,
  },
  actions: {
    setBusy: () => {},
    setErrorMessage: () => {},
    setPaymentMethod: () => {},
    setBillingInterval: () => {},
    setTeamSize: () => {},
    setCoupon: () => {},
    beforeSubmit: () => {},
    onSubmit: () => {},
    afterSubmit: () => {},
    onPaymentMethodChange: () => {},
    setHasFormError: () => {},
  },
};

const CheckoutContext = createContext(initialState);

const resolveQueryPlan = (plan) => {
  return plan === PlanEnum.STUDENT ? PlanEnum.RESEARCHER : resolvePlan(plan);
};

const resolveTeamSize = (teamSize, plan) => {
  return plan === PlanEnum.LAB_RESEARCHER && teamSize ? parseInt(teamSize) : 1;
};

const validateUserCountry = (userCountry) => {
  if (!!userCountry && userCountry.toLowerCase() !== 'null') {
    return true;
  }

  return false;
};

/**
 * @typedef {import('utils/plan').PlanID} PlanID
 * @param {PlanID} plan - The plan ID
 * @param {string} userCountry - The user country code
 * @param {number} teamSize - The team size
 * @returns
 */
const getPricingData = (
  plan = PlanEnum.RESEARCHER,
  userCountry = DEFAULT_LOCALE_ID,
  teamSize = 1,
) => {
  const isUserCoutryValid = validateUserCountry(userCountry);
  const countryCode = isUserCoutryValid ? userCountry : DEFAULT_LOCALE_ID;
  const localeData = getLocaleData(countryCode);
  const plansPricingData = getPlansPricingData({
    plansIds: [plan],
    countriesCodes: [countryCode],
    teamSize,
  });

  const cyclesData = plansPricingData[plan].locales[localeData.id].cycles;
  return { id: plan, label: plansPricingData[plan].label, cycles: cyclesData };
};

function CheckoutProvider({ children }) {
  const router = useRouter();
  const isMobile = useIsMobile();

  const { user, isAuthenticated, profile, validateSession } = useAuthUser();
  const profileService = new ProfileService();
  const [currentPlan] = usePlan();

  const stripe = useStripe();
  const stripeElements = useElements();
  const paymentService = new PaymentService();

  const [plan, setPlan] = useState(
    router.query.plan ? resolveQueryPlan(router.query.plan) : PlanEnum.RESEARCHER,
  );
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethodEnum.CARD);
  const [teamSize, setTeamSize] = useState(router.query.teamSize);
  const [billingInterval, setBillingInterval] = useState(BillingIntervalEnum.YEAR);
  const [coupon, setCoupon] = useState(null);
  const [planData, setPlanData] = useState(
    () => getPricingData(plan, user.country_code, teamSize).cycles,
  );

  const [isBusy, setIsBusy] = useState(false);
  const [hasFormError, setHasFormError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef(null);
  const clevertap = getCleverTap();
  const { initialPath } = router.query;

  function sendClevertapEventViewedPage() {
    clevertap.event.push('Payment check-out page viewed', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: router.asPath,
      Description: 'Viewed Checkout Page',
    });
  }

  function handleError(error) {
    let errMessage = 'Could not process payment.';
    if (error instanceof PaymentError || error?.response?.status === 400) {
      errMessage = error instanceof PaymentError ? error.message : error.response.data.message;

      setErrorMessage(errMessage);
    } else {
      setErrorMessage(
        'Could not process your payment. Try again with the selected payment option or choose other.',
      );
    }

    console.log('Payment error:', error);

    const metadata = {
      email: user.email,
      user_id: user.id,
      error: error.message || error?.response?.data?.message,
      response: error.response || null,
    };

    // if (window.Intercom) {
    //   window.Intercom('trackEvent', errMessage, metadata);
    // }

    paymentService.createPaymentInfo({
      gateway: paymentMethod === PaymentMethodEnum.CARD ? 'Stripe' : 'PayPal',
      total: planData.priceInUSD,
      currency: 'USD',
      event: 'subscription_fail',
    });
  }

  function handlePaymentMethodChange(event) {
    const method = event.currentTarget.getAttribute('method');
    setPaymentMethod(method);
    setErrorMessage('');
  }

  async function handleSetupIntent(id) {
    const { data: setupIntent } = await paymentService.getStripeSetupIntent(id);
    const { payment_method: paymentMethod } = setupIntent;

    const response = await paymentService.stripe({
      plan,
      billingInterval,
      paymentMethodId: paymentMethod,
      paymentMethodType: PaymentMethodEnum.ALIPAY,
      couponCode: coupon?.code,
    });

    if (response.data.status === 'active') {
      handleSucceededPayment();
    } else {
      console.log('subscription error', response.data);
    }
  }

  function validateCancellation(currentPlan, currentDate) {
    let cancelAtDate = new Date(currentPlan?.cancelAt);

    if (cancelAtDate && cancelAtDate > currentDate) {
      let formattedDate = moment(cancelAtDate).format('MM/DD/YYYY');
      const errorMessage = `It was not possible to complete the purchase because you are already registered until ${formattedDate}.`;

      return errorMessage;
    }

    return null;
  }

  function sendSubmitTrackingEvents() {
    ReactGA.event({
      category: 'checkout',
      action: `order_clicked_${planData.label}`,
      label: 'When the Complete Subscription button is clicked',
      value: user.id,
    });

    ReactGA.event({
      category: 'checkout',
      action: `order_clicked_${billingInterval}`,
      label: 'When the Complete Subscription button is clicked',
      value: user.id,
    });
  }

  async function handleBeforeSubmit(formData) {
    if (formData?.fields?.whatsappPhoneNumber?.value) {
      await profileService.update(user.id, {
        whatsappNumber: formData.fields.whatsappPhoneNumber.value,
      });
    }

    let currentDate = new Date();
    const cancellationError = validateCancellation(currentPlan, currentDate);

    if (cancellationError) {
      setErrorMessage(cancellationError);
      return;
    }

    sendSubmitTrackingEvents();
    setErrorMessage('');
    setIsBusy(true);
  }

  async function handleSubmit() {
    handleBeforeSubmit();

    const isValidSession = await validateSession({
      triggerLogin: true,
      backgroundValidation: true,
    });

    if (!isValidSession) {
      return;
    }

    if (errorMessage) {
      return;
    }

    if (paymentMethod === PaymentMethodEnum.PAYPAL) {
      try {
        const response = await paymentService.paypal({
          plan,
          teamSize: plan === PlanEnum.LAB_RESEARCHER ? teamSize : undefined,
          billingInterval,
          applicationContext: {
            returnUrl: makeSucceededPaymentRedirectUrl({ fullUrl: true }),
            cancelUrl: `${
              window.location.origin
            }/my-account/billing/checkout?failedPayment=1&${queryString.stringify({
              plan,
              billingInterval,
              ...(coupon && { couponCode: coupon.code }),
            })}`,
          },
        });

        const redirectLink = response.data.links.find((link) => link.rel === 'approve');

        if (!redirectLink) {
          throw new Error('Could not find PayPal payment approval link');
        }

        window.location.href = redirectLink.href;
      } catch (e) {
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }

    if (paymentMethod === PaymentMethodEnum.ALIPAY) {
      try {
        const { data } = await paymentService.createOneTimeSubscription({
          plan,
          billingInterval,
          paymentMethod: PaymentMethodEnum.ALIPAY,
        });
        const { error } = await stripe.confirmAlipayPayment(data.client_secret, {
          return_url: makeSucceededPaymentRedirectUrl({ fullUrl: true }),
        });

        if (error) {
          throw new PaymentError(error);
        }
      } catch (e) {
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }

    if (paymentMethod === PaymentMethodEnum.WECHAT_PAY) {
      try {
        const { data } = await paymentService.createOneTimeSubscription({
          plan,
          billingInterval,
          paymentMethod: PaymentMethodEnum.WECHAT_PAY,
        });
        const { error, paymentIntent } = await stripe.confirmWechatPayPayment(data.client_secret, {
          payment_method_options: {
            wechat_pay: {
              client: 'web',
            },
          },
        });

        if (error) {
          throw new PaymentError(error);
        }

        if (paymentIntent.status !== 'succeeded') {
          throw new PaymentError('Payment failed. Please try again.');
        }

        handleSucceededPayment();
      } catch (e) {
        handleError(e);
      } finally {
        setIsBusy(false);
      }
    }
  }

  async function handleAfterSubmit({ success, data = null, paymentMethod, error = null }) {
    if (!success) {
      handleError(error);
    } else if (data.status === 'active') {
      handleSucceededPayment(data);
    } else {
      await handlePaymentThatRequiresCustomerAction(data, paymentMethod);
    }

    setIsBusy(false);
  }

  async function handlePaymentThatRequiresCustomerAction(subscription, paymentMethod) {
    const paymentIntent = subscription.latest_invoice.payment_intent;
    const needsCardAuthentication =
      paymentIntent.status === 'requires_action' ||
      (subscription.paymentAttempts >= 1 && paymentIntent.status === 'requires_payment_method');

    if (needsCardAuthentication) {
      const { error, paymentIntent: newPaymentIntent } = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: paymentMethod.id,
          setup_future_usage: 'off_session',
        },
      );

      if (error) {
        throw new PaymentError(error.message);
      }

      if (newPaymentIntent.status === 'succeeded') {
        handleSucceededPayment();
      } else {
        throw new PaymentError('Card authentication failed. Try with another.');
      }
    }

    throw new Error('Unexpected error');
  }

  function makeSucceededPaymentRedirectUrl(
    options = { pendingConfirmation: false, fullUrl: false },
  ) {
    let url;

    if (options.pendingConfirmation) {
      url = `/my-account/overview?processingPayment=1&plan=${plan}`;
    } else {
      url = `/my-account/overview?succededPayment=1&plan=${plan}${
        initialPath ? `&initialPath=${initialPath}` : ''
      }`;
    }

    if (coupon?.context) {
      url += `&couponContext=${coupon.context}`;
    }

    return options.fullUrl ? `${window.location.origin}/app${url}` : url;
  }

  function handleSucceededPayment() {
    ReactGA.event({
      category: 'checkout',
      action: `payment_succeeded_${planData.label}`,
      label: 'When the subscription is complete',
      value: user.id,
    });

    ReactGA.event({
      category: 'checkout',
      action: `payment_succeeded_${billingInterval}`,
      label: 'When the subscription is complete',
      value: user.id,
    });

    const clevertapEventType =
      billingInterval === BillingIntervalEnum.YEAR ? 'Annually' : 'Monthly';

    clevertap.event.push('Purchase', {
      Product: 'MTG',
      Email: user.email,
      URL: router.asPath,
      'Country code': user.country_code,
      'Billing Interval': billingInterval,
      Plan: planData.label,
      Description: 'Payment Succeeded',
      Locale: 'checkout',
      Type: clevertapEventType,
      Category: planData.label,
    });

    if (coupon?.context === 'black_november') {
      ReactGA.event({
        category: 'checkout',
        action: 'black_november_order_finished',
        label: 'when payment for the black november promotion is successfully completed',
        value: user.id,
      });

      clevertap.event.push('Purchase', {
        Product: 'MTG',
        Email: user.email,
        'Country code': user.country_code,
        URL: router.asPath,
        'Billing Interval': billingInterval,
        Plan: planData.label,
        Description: 'Black Friday Payment Succeeded',
        Locale: 'checkout',
        coupon: 'black_november',
      });
    }

    router.push(makeSucceededPaymentRedirectUrl());
  }

  useEffect(() => {
    if (router.query.plan) {
      setPlan(resolveQueryPlan(router.query.plan));
    }

    setBillingInterval(
      resolveBillingInterval(router.query.billingInterval, resolvePlan(router.query.plan)),
    );

    setTeamSize(resolveTeamSize(router.query.teamSize, router.query.plan));

    if (router.query.failedPayment === '1') {
      setHasUnknownError(true);
    }

    if (
      isAuthenticated &&
      router.query.redirect_status === 'succeeded' &&
      router.query.setup_intent &&
      router.query.setup_intent_client_secret
    ) {
      handleSetupIntent(router.query.setup_intent);
    }
  }, [router.query, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const userCountry = validateUserCountry(user.country_code)
        ? user.country_code
        : DEFAULT_LOCALE_ID;
      const pricingData = getPricingData(plan, userCountry, teamSize);
      setPlanData(pricingData.cycles);
    }
  }, [user, billingInterval, teamSize, plan]);

  useEffect(() => {
    if (coupon) {
      const isCouponValidForLocale = checkLocaleCouponForUserCountry(
        coupon.context,
        user.country_code,
      );

      if (!isCouponValidForLocale) {
        setCoupon(null);
      }
    }
  }, [coupon, user.country_code]);

  useEffect(() => {
    async function fetchPaymentServiceCountry() {
      try {
        const { data } = await paymentService.data();
        const isJapanOrChina = data.countryCode === 'CN' || data.countryCode === 'JP';

        if (isJapanOrChina) {
          router.push('/pricing');
        }
      } catch (e) {
        console.error(e);
      }
    }

    sendClevertapEventViewedPage();
    fetchPaymentServiceCountry();
  }, []);

  const value = {
    data: {
      isBusy,
      errorMessage,
      user: {
        name: profile?.name || '',
        email: user?.email || '',
        countryCode: user.country_code,
        ready: isAuthenticated,
      },
      planData,
      coupon,
      profile: { whatsappNumber: profile.whatsappNumber },
      billingInterval,
      teamSize,
      paymentService,
      profileService,
      paymentMethod,
      stripe,
      stripeElements,
      isMobile,
      currentPlan,
      formRef,
      errorMessage,
      plan,
      hasFormError,
    },
    actions: {
      setIsBusy,
      setErrorMessage,
      setPaymentMethod,
      setCoupon,
      setHasFormError,
      setPlan,
      setBillingInterval,
      setTeamSize,
      beforeSubmit: handleBeforeSubmit,
      onSubmit: handleSubmit,
      afterSubmit: handleAfterSubmit,
      onPaymentMethodChange: handlePaymentMethodChange,
    },
  };

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
}

export function CheckoutProviderWrapper({ children }) {
  return (
    <StripeProvider>
      <CheckoutProvider>{children}</CheckoutProvider>
    </StripeProvider>
  );
}

export function useCheckoutContext() {
  const value = useContext(CheckoutContext);
  return value;
}
